import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { Button, Callout, Column, DataTable, FilterPickerGroup, IconButton, Page, Searchbox, Spacing, useDevice } from 'seagull';
import { Settings } from 'seagull/icons/essentials';
import { getTestIds } from 'seagull/utils/TestIds';
import { AiFilterChip } from '~/components/AiSearchChips/AiFilterChip/AiFilterChip';
import TableFilters from '~/components/TableFilters/TableFilters';
import { useSetDocumentTitle } from '~/hooks/useSetDocumentTitle';
import { useLocaleKeys } from '~/i18n';
import { useRevenueOpportunitiesPage } from './hooks/useRevenueOpportunitiesPage/useRevenueOpportunitiesPage';
import { seeAllTable } from './RevenueOpportunities.utils';
export const GetRevenueOpportunitiesTID = (parentTestId)=>getTestIds(parentTestId);
const PAGE_ON_PHONE = {
    showActions: true
};
export const RevenueOpportunities = ()=>{
    const { RevenueOpportunitiesTestIds: TID } = useMemo(()=>GetRevenueOpportunitiesTID(), []);
    const { t: texts } = useLocaleKeys('ai.pulse');
    // eslint-disable-next-line no-restricted-syntax -- WHY: the page is the layout boundary; phones get the sheet filters, an icon header action and card fields (frontend-mobile.md §3b)
    const { isMobileDevice } = useDevice();
    const { filters, rows, rowActions, lastSyncedDate, tableState, hasError, canEditPreferences, tableSort, pagination, changeSort, retry, goBack, openPreferences } = useRevenueOpportunitiesPage(TID.Toasts);
    useSetDocumentTitle(texts.seeAll.title());
    const { mainColumn, columns, mobileCard } = useMemo(()=>seeAllTable(texts.seeAll, isMobileDevice, TID.ConfidenceBars), [
        texts.seeAll,
        isMobileDevice,
        TID.ConfidenceBars
    ]);
    const editPreferences = isMobileDevice ? /*#__PURE__*/ _jsx(IconButton, {
        bi: {
            name: 'pulse_see_all_edit_preferences'
        },
        testId: TID.EditPreferences(),
        skin: "primaryTransparentBoxed",
        size: "smallMedium",
        "aria-label": texts.seeAll.editPreferences(),
        onClick: openPreferences,
        children: /*#__PURE__*/ _jsx(Settings, {
            size: 16
        })
    }) : /*#__PURE__*/ _jsx(Button, {
        bi: {
            name: 'pulse_see_all_edit_preferences'
        },
        testId: TID.EditPreferences(),
        skin: "primaryOutline",
        onClick: openPreferences,
        children: texts.seeAll.editPreferences()
    });
    return /*#__PURE__*/ _jsx(Page, {
        testId: TID.Root(),
        header: texts.seeAll.title(),
        subheader: lastSyncedDate == null ? undefined : texts.section.lastSynced({
            date: lastSyncedDate
        }),
        onClickBack: goBack,
        actions: canEditPreferences && editPreferences,
        mobile: PAGE_ON_PHONE,
        children: /*#__PURE__*/ _jsxs(Column, {
            gap: Spacing.Space16,
            children: [
                isMobileDevice ? /*#__PURE__*/ _jsx(TableFilters, {
                    testIdPrefix: TID.Filters.Phone(),
                    search: {
                        bi: {
                            name: 'pulse_see_all_search'
                        },
                        testId: TID.Filters.Search(),
                        value: filters.search,
                        placeholder: texts.seeAll.searchPlaceholder(),
                        debounceTime: filters.searchDebounceMs,
                        onChange: (event)=>filters.changeSearch(event.target.value),
                        onClear: ()=>filters.changeSearch('')
                    },
                    filterLabels: filters.phoneLabels,
                    filterConfig: filters.phoneConfig,
                    selectedFilters: filters.phoneSelection,
                    setSelectedFilters: filters.setPhoneFilters,
                    onRefresh: (next, sort)=>filters.applyPhoneFilters(next, sort),
                    mobile: {
                        sortOptions: filters.phoneSortOptions,
                        sort: filters.phoneSort
                    }
                }) : /*#__PURE__*/ _jsxs(FilterPickerGroup, {
                    flexWrap: true,
                    testId: TID.Filters.Root(),
                    children: [
                        /*#__PURE__*/ _jsx(Searchbox, {
                            bi: {
                                name: 'pulse_see_all_search'
                            },
                            testId: TID.Filters.Search(),
                            placeholder: texts.seeAll.searchPlaceholder(),
                            value: filters.search,
                            debounceTime: filters.searchDebounceMs,
                            onChange: (event)=>filters.changeSearch(event.target.value),
                            onClear: ()=>filters.changeSearch('')
                        }),
                        /*#__PURE__*/ _jsx(AiFilterChip, {
                            mode: "checkboxes",
                            testId: TID.Filters.Type(),
                            biPrefix: "pulse_see_all_type",
                            fieldLabel: texts.seeAll.filters.type(),
                            items: filters.kindOptions,
                            selected: filters.kinds,
                            operator: "include",
                            emptyValueLabel: texts.seeAll.filters.all(),
                            singleValuePrefix: texts.seeAll.filters.is(),
                            onApply: (param)=>{
                                let { selected } = param;
                                return filters.changeKinds(selected);
                            }
                        }),
                        /*#__PURE__*/ _jsx(AiFilterChip, {
                            mode: "checkboxes",
                            testId: TID.Filters.Service(),
                            biPrefix: "pulse_see_all_service",
                            fieldLabel: texts.seeAll.filters.service(),
                            items: filters.serviceOptions,
                            selected: filters.serviceKeys,
                            operator: "include",
                            searchable: true,
                            emptyValueLabel: texts.seeAll.filters.all(),
                            singleValuePrefix: texts.seeAll.filters.is(),
                            onApply: (param)=>{
                                let { selected } = param;
                                return filters.changeServices(selected);
                            }
                        })
                    ]
                }),
                hasError ? /*#__PURE__*/ _jsx(Callout, {
                    intent: "error",
                    testId: TID.Error.Root(),
                    withAction: /*#__PURE__*/ _jsx(Button, {
                        bi: {
                            name: 'pulse_see_all_retry'
                        },
                        testId: TID.Error.TryAgain(),
                        skin: "monochrome",
                        size: "small",
                        onClick: retry,
                        children: texts.section.retry()
                    }),
                    children: texts.section.error()
                }) : /*#__PURE__*/ _jsx(DataTable, {
                    testId: TID.Table(),
                    items: rows,
                    getRowId: (row)=>row.id,
                    mainColumn: mainColumn,
                    columns: columns,
                    mobile: mobileCard,
                    rowActions: {
                        inline: (row)=>[
                                {
                                    label: row.actionLabel,
                                    onClick: ()=>rowActions.act(row),
                                    bi: {
                                        name: row.actionBiName,
                                        data: row.biData
                                    },
                                    disabled: rowActions.isDismissing(row.id)
                                }
                            ],
                        menu: (row)=>rowActions.dismissChoices(row).map((param)=>{
                                let { option, label, biName } = param;
                                return {
                                    label,
                                    onClick: ()=>rowActions.dismiss(row, option),
                                    bi: {
                                        name: biName,
                                        data: row.biData
                                    },
                                    disabled: rowActions.isDismissing(row.id)
                                };
                            }),
                        menuBi: {
                            name: 'pulse_see_all_row_menu'
                        },
                        menuTitle: (row)=>texts.section.amountPerYear({
                                amount: row.amount
                            }),
                        menuSubtitle: (row)=>row.lead
                    },
                    sort: _object_spread_props(_object_spread({}, tableSort), {
                        onChange: changeSort
                    }),
                    pagination: pagination,
                    state: tableState,
                    emptyState: texts.seeAll.empty()
                })
            ]
        })
    });
};
